import { EditOutlined, PlusOutlined, DeleteOutlined, ExclamationCircleOutlined, SaveOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { buildQueryString, showError, showSuccess, trimData } from "../../../utils/utilities";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { cloneDeep, get, toLower } from "lodash";
import { dbDate, langDate, paginationDefaults } from "../../../utils/properties";
import { Col, DatePicker, Modal, Row, Select, Spin, Table } from "antd";
import { setFilters } from "../../../redux/ducks/Filters";
import { useFormik } from "formik";
import { setLoading } from "../../../redux/ducks/Loading";
import Filter from "../../../components/functional_components/Filter/Filter";
import Button from "../../../components/functional_components/Button/PFButton";
import CarUser from "../../../services/api/CarUser";
import User from "../../../services/api/User";
import dayjs from "dayjs";
const { Option } = Select;
const { confirm } = Modal;

const ParkAutoAssignation = ({ parkAuto }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const params = useParams();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langDate[toLower(currentLanguage)];
  const [state, setState] = useState({
    tableContent: [],
    hrList: [],
    hrSearchList: [],
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    modal: {
      visible: false,
      type: "",
      data: {},
    },
    loading: false,
  });
  useEffect(() => {
    const queryString = buildQueryString(filters.obj, { "parkAuto.id": params.carId });
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  }, []);
  const formikCarUser = useFormik({
    initialValues: {
      periodStart:
        get(state.modal.data, "periodStart", null) !== null ? dayjs(state.modal.data.periodStart, dbDate) : null,
      periodEnd: get(state.modal.data, "periodEnd", null) !== null ? dayjs(state.modal.data.periodEnd, dbDate) : null,
      user: get(state.modal.data, "user.username", null),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.periodStart) {
        errors.periodStart = "Required!";
      }
      if (!values.periodEnd) {
        errors.periodEnd = "Required!";
      }
      if (!values.user) {
        errors.user = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      const data = {
        periodStart: dayjs(values.periodStart, dateFormat).format(dbDate),
        periodEnd: dayjs(values.periodEnd, dateFormat).format(dbDate),
        parkAuto: parkAuto,
        user: state.hrList.find((h) => h.username === values.user),
        id: state.modal.data?.id || undefined,
      };
      const saveResponse = await CarUser.setCarUser(data);
      if (!saveResponse.error) {
        showSuccess();
        closeModal();
        const queryString = buildQueryString(filters.obj, { "parkAuto.id": params.carId });
        dispatch(setFilters(queryString));
        dataList(queryString.string);
      } else {
        showError(get(saveResponse, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const dataList = async (queryString) => {
    dispatch(setLoading(true));
    const { data, error, currentPage, pageSize, totalItems } = await CarUser.getCarUsers(queryString);
    if (!error && data) {
      data.forEach((cc, i) => {
        cc.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: data || [],
        pagination: {
          currentPage: currentPage ? currentPage + 1 : paginationDefaults.page,
          size: pageSize || paginationDefaults.size,
          totalItems: totalItems || paginationDefaults.totalItems,
        },
      }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const closeModal = () => {
    formikCarUser.resetForm();
    setState((prevState) => ({ ...prevState, modal: { visible: false, type: "", data: {} } }));
  };

  const editCallback = async (id) => {
    setState((prevState) => ({ ...prevState, loading: true, modal: { visible: true, type: "edit" } }));
    const { data, error } = await CarUser.getCarUser(id);
    if (!error && data) {
      setState((prevState) => ({ ...prevState, modal: { visible: true, type: "edit", data }, hrList: [data.user] }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const onSearchusers = async (value, filter = false) => {
    if (value.length >= 2) {
      const { error, data } = await User.getUsersInternalHrList(`allPages=true&surname=${value}`);
      if (!error && data) {
        if (filter) {
          setState((prevState) => ({
            ...prevState,
            hrSearchList: data.map((u) => ({ value: u.username, label: `${u.name} ${u.surname}` })),
          }));
        } else {
          setState((prevState) => ({
            ...prevState,
            hrList: data,
          }));
        }
      }
    } else {
      setState((prevState) => ({ ...prevState, hrList: [], hrSearchList: [] }));
    }
  };

  const deleteCallback = (id) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const deleteResponse = await CarUser.deleteCarUser(id);
        if (!deleteResponse.error) {
          showSuccess();
          const queryString = buildQueryString(filters.obj, { "parkAuto.id": params.carId });
          dispatch(setFilters(queryString));
          dataList(queryString.string);
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };

  const columns = [
    {
      title: t("carUser.table.periodStart"),
      dataIndex: "periodStart",
      key: "periodStart",
      render: (periodStart) => dayjs(periodStart, dbDate).format(langDate[toLower(currentLanguage)]),
    },
    {
      title: t("carUser.table.periodEnd"),
      dataIndex: "periodEnd",
      key: "periodEnd",
      render: (periodEnd) => dayjs(periodEnd, dbDate).format(langDate[toLower(currentLanguage)]),
    },
    {
      title: t("carUser.table.user"),
      dataIndex: "user",
      key: "user",
      render: (_, data) => `${data.user.name} ${data.user.surname}`,
    },
    {
      title: t("general.actions"),
      key: "action",
      width: 80,
      render: (data) => {
        return (
          <div className="space-around">
            <div onClick={() => editCallback(data.id)}>
              <EditOutlined className="icon-medium" />
            </div>
            <div onClick={() => deleteCallback(data.id)}>
              <DeleteOutlined className="icon-medium" />
            </div>
          </div>
        );
      },
    },
  ];

  const formikFilter = useFormik({
    initialValues: {
      periodStart: "",
      periodEnd: "",
      user: "",
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      // When I search, I start my page to 0
      let dataFilter = cloneDeep(values);
      dataFilter["page"] = 0;
      dataFilter["parkAuto.id"] = params.carId;
      if (values.periodStart) {
        dataFilter.periodStart =
          dataFilter.periodStart !== "" ? dayjs(dataFilter.periodStart, dateFormat).format(dbDate) : undefined;
      } else {
        delete dataFilter.periodStart;
      }
      if (values.periodEnd) {
        dataFilter.periodEnd =
          dataFilter.periodEnd !== "" ? dayjs(dataFilter.periodEnd, dateFormat).format(dbDate) : undefined;
      } else {
        delete dataFilter.periodEnd;
      }
      dataFilter["user.username"] = values.user;
      delete dataFilter.user;
      const queryString = buildQueryString(filters.obj, dataFilter);
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    },
  });

  const fieldsFilter = [
    {
      type: "date",
      allowClear: true,
      isRequired: false,
      id: "periodStart",
      name: "periodStart",
      format: dateFormat,
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("carUser.table.periodStart"),
      value: formikFilter.values.periodStart,
      onChange: (date) => {
        formikFilter.setFieldValue("periodStart", date);
      },
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "date",
      isRequired: false,
      id: "periodEnd",
      name: "periodEnd",
      format: dateFormat,
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("carUser.table.periodEnd"),
      value: formikFilter.values.periodEnd,
      onChange: (date) => {
        formikFilter.setFieldValue("periodEnd", date);
      },
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "selectWithSearch",
      isRequired: false,
      id: "user",
      name: "user",
      value: formikFilter.values.user,
      label: t("carUser.table.user") + " " + t("carUser.userLabelInfo"),
      onChange: formikFilter.handleChange("user"),
      onBlur: formikFilter.handleBlur("user"),
      onSearch: (e) => onSearchusers(e, true),
      dataSelect: state.hrSearchList,
    },
  ];

  const resetFilter = () => {
    const queryString = buildQueryString(
      { page: paginationDefaults.page - 1, size: paginationDefaults.size },
      { "parkAuto.id": params.carId }
    );
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  };

  return (
    <>
      <Filter formikForm={formikFilter} fields={fieldsFilter} onReset={resetFilter} allInOneRow />
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={() => setState((prevState) => ({ ...prevState, modal: { visible: true, type: "new" } }))}
        />
      </Row>
      <Table
        dataSource={state.tableContent}
        columns={columns}
        size="small"
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            const queryString = buildQueryString(filters.obj, {
              page: page - 1,
              size: pageSize,
              "parkAuto.id": params.carId,
            });
            dispatch(setFilters(queryString));
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                pageSize: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            dataList(queryString.string);
          },
        }}
      />
      <Modal
        open={state.modal.visible}
        width={700}
        title={
          state.modal.type === "edit" && state.modal.data && state.modal.data.brand
            ? state.modal.data.brand
            : t("carUser.new")
        }
        footer={[
          <Button
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikCarUser.submitForm}
            isDisabled={!(formikCarUser.dirty && formikCarUser.isValid)}
          />,
        ]}
        onCancel={closeModal}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 24]} justify="space-around">
          <Col lg={22} md={22} xs={24} sm={22}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="user">
                {t("carUser.table.user")}*
              </label>
            </Row>
            <Row>
              <p>{t("carUser.userLabelInfo")}</p>
            </Row>
            <Row>
              <Select
                allowClear
                onClear={() => {
                  setState((prevState) => ({ ...prevState, hrList: [] }));
                }}
                className="w-100"
                id="user"
                optionFilterProp="value"
                name="user"
                showSearch
                placeholder={t("carUser.table.user")}
                onChange={(value) => formikCarUser.setFieldValue("user", value)}
                onBlur={() => {
                  formikCarUser.handleBlur("user");
                }}
                onSearch={(value) => onSearchusers(value)}
                filterOption={(input, option) => option.name.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                value={formikCarUser.values.user}
              >
                {state.hrList.length > 0 &&
                  state.hrList.map((user, idx) => {
                    return (
                      <Option key={idx} value={user.username} name={user.surname}>
                        {user.name} {user.surname}
                      </Option>
                    );
                  })}
              </Select>
            </Row>
          </Col>
        </Row>
        <Row gutter={[40, 24]} justify="space-around">
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="periodStart">
                {t("carUser.table.periodStart")}*
              </label>
            </Row>
            <Row>
              <DatePicker
                className="w-100"
                allowClear={false}
                id="periodStart"
                name="periodStart"
                format={dateFormat}
                locale={langDate[`locale_${toLower(currentLanguage)}`]}
                onChange={(date) => {
                  formikCarUser.setFieldValue("periodStart", date);
                }}
                onBlur={formikCarUser.handleBlur}
                value={formikCarUser.values.periodStart}
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="periodEnd">
                {t("carUser.table.periodEnd")}*
              </label>
            </Row>
            <Row>
              <DatePicker
                className="w-100"
                allowClear={false}
                id="periodEnd"
                name="periodEnd"
                format={dateFormat}
                locale={langDate[`locale_${toLower(currentLanguage)}`]}
                onChange={(date) => {
                  formikCarUser.setFieldValue("periodEnd", date);
                }}
                onBlur={formikCarUser.handleBlur}
                value={formikCarUser.values.periodEnd}
                disabledDate={(current) => current && current < dayjs(formikCarUser.values.periodStart)}
              />
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default ParkAutoAssignation;
